import type {
  Answer,
  Capability,
  Question,
  ReportSnapshot,
  ResolvedAnswer,
  Topic,
} from "../../api/domain.ts";
import { productNames } from "./report-view.ts";
import type { Named } from "./report-view.ts";

/**
 * Turning one evaluation payload into the axes of a two-product comparison.
 *
 * As everywhere on this screen, nothing here scores anything: the server
 * already produced every number. This module only picks which of them face each
 * other on an axis, and which axes are worth showing first.
 */

/** The level the axes are read at. Topics aggregate; capabilities are the detail. */
export type CompareLevel = "topic" | "capability";

/** How many axes the chart opens with before the reader narrows it down. */
export const axisBudget = 12;

/** The scale every score on this screen lives on. */
export const scoreMax = 5;

/** One axis with the two scores that meet on it. Either may be missing. */
export type CompareAxis = {
  id: string;
  label: string;
  /** The topic a capability belongs to; the axis's own name at topic level. */
  group: string;
  a: number | null;
  b: number | null;
};

const nameFrom = (entries: Named[], id: string): string =>
  entries.find((entry) => entry.id === id)?.label ?? id;

/**
 * Every axis of the chosen level with both products' scores, in catalogue order.
 *
 * An axis neither product has been assessed on is left out entirely rather than
 * drawn empty: it says nothing about either of them, and an axis at the centre
 * of the wheel reads as a weakness the data never claimed.
 */
export const compareAxes = (
  snapshot: ReportSnapshot,
  level: CompareLevel,
  a: string,
  b: string,
): CompareAxis[] => {
  const scores = (product: string) => scoresOf(snapshot, level, product);
  const left = scores(a);
  const right = scores(b);

  return axisCatalogue(snapshot, level)
    .map((axis) => ({
      ...axis,
      a: left.get(axis.id) ?? null,
      b: right.get(axis.id) ?? null,
    }))
    .filter((axis) => axis.a !== null || axis.b !== null);
};

/** The axes the level offers at all, named and grouped, before any scoring. */
const axisCatalogue = (snapshot: ReportSnapshot, level: CompareLevel): Omit<CompareAxis, "a" | "b">[] => {
  if (level === "topic") {
    return snapshot.input.topics.map((topic) => ({
      id: topic.id,
      label: topic.title,
      group: topic.title,
    }));
  }

  const topics = snapshot.input.topics;
  return snapshot.input.capabilities.map((capability) => ({
    id: capability.id,
    label: capability.label,
    group: topics.find((topic) => topic.id === capability.topic)?.title ?? capability.topic,
  }));
};

/** One product's score per axis of the chosen level, keyed by axis id. */
const scoresOf = (
  snapshot: ReportSnapshot,
  level: CompareLevel,
  product: string,
): Map<string, number | null> => {
  const evaluated = snapshot.evaluation.products.find((entry) => entry.product === product);
  if (evaluated === undefined) return new Map();

  if (level === "topic") {
    return new Map(evaluated.topics.map((topic) => [topic.topic, topic.score]));
  }

  return new Map(
    evaluated.topics.flatMap((topic) =>
      topic.capabilities.map(
        (capability) => [capability.capability, capability.score] as const,
      ),
    ),
  );
};

/**
 * How far apart the two products are on an axis, or `null` when that cannot be
 * measured because one of them has no score there.
 */
export const divergence = (axis: CompareAxis): number | null =>
  axis.a === null || axis.b === null ? null : Math.abs(axis.a - axis.b);

/**
 * Widest distance first, with equal distances falling back to the label so the
 * same data always renders in the same order.
 *
 * An axis only one product is assessed on shows a gap rather than a gap in
 * quality, so it sorts behind every axis whose distance is a real number.
 */
const impactOrder = (left: CompareAxis, right: CompareAxis): number => {
  const gap = divergence(left);
  const other = divergence(right);
  if (gap === null && other === null) return left.label.localeCompare(right.label);
  if (gap === null) return 1;
  if (other === null) return -1;
  if (gap === other) return left.label.localeCompare(right.label);
  return other - gap;
};

/**
 * Axes ranked by how much they separate the pair.
 *
 * The chart opens on the head of this order, and an opened row breaks down into
 * the same order one level further in, so "what matters most here" means the
 * same thing wherever the reader is looking.
 */
export const byImpact = <T extends CompareAxis>(axes: readonly T[]): T[] =>
  [...axes].sort(impactOrder);

/** The axes to open on: the ones where the two products differ most. */
export const mostDivergent = (axes: CompareAxis[], limit = axisBudget): string[] =>
  byImpact(axes)
    .slice(0, limit)
    .map((axis) => axis.id);

/**
 * Whether two axis lists say the same thing.
 *
 * Rebuilding the list from the snapshot yields a fresh array every time any
 * part of the selection moves, folding a row open included. Comparing the
 * contents instead of the array lets the screen leave the rows alone when
 * nothing about them changed, which is what keeps focus on a disclosure the
 * reader just pressed.
 */
export const sameAxes = (left: readonly CompareAxis[], right: readonly CompareAxis[]): boolean =>
  left.length === right.length &&
  left.every((axis, index) => {
    const other = right[index];
    return (
      other !== undefined && axis.id === other.id && axis.a === other.a && axis.b === other.b
    );
  });

/** The picked axes back in catalogue order, so the wheel never reshuffles. */
export const inCatalogueOrder = (axes: CompareAxis[], picked: readonly string[]): CompareAxis[] => {
  const wanted = new Set(picked);
  return axes.filter((axis) => wanted.has(axis.id));
};

/** The products a comparison can be built from, best first. */
export const comparableProducts = (snapshot: ReportSnapshot): Named[] => {
  const names = productNames(snapshot);
  return [...snapshot.evaluation.products]
    .sort((left, right) => (right.score ?? -1) - (left.score ?? -1))
    .map((product) => ({ id: product.product, label: nameFrom(names, product.product) }));
};

/** The pair to open on: the two best-scoring products, or as many as there are. */
export const openingPair = (snapshot: ReportSnapshot): { a: string; b: string } | undefined => {
  const products = comparableProducts(snapshot);
  if (products.length < 2) return undefined;
  return { a: products[0]!.id, b: products[1]!.id };
};

/** A product's overall score, for the legend beside its name. */
export const overallScore = (snapshot: ReportSnapshot, product: string): number | null =>
  snapshot.evaluation.products.find((entry) => entry.product === product)?.score ?? null;

/** Which side of a comparison stands higher on an axis. */
export type CompareLeader = "a" | "b";

/**
 * An axis the pair genuinely splits on: both sides scored, and not by the same
 * number. The gap and the leading side are resolved once here, so the list that
 * renders them never recomputes a difference the reader is looking at.
 */
export type CompareDifference = CompareAxis & {
  gap: number;
  leader: CompareLeader;
};

/**
 * The axes the pair splits on, widest gap first.
 *
 * Only a measurable difference qualifies. An axis where one product has no
 * score cannot be a difference in quality, and an axis both products land on
 * equally is agreement rather than a difference; both are counted separately so
 * the reader still knows they exist. Ties on the gap fall back to the label, so
 * the order is stable between renders of the same data.
 */
export const biggestDifferences = (axes: readonly CompareAxis[]): CompareDifference[] =>
  axes
    .flatMap((axis) => {
      const { a, b } = axis;
      if (a === null || b === null || a === b) return [];
      const leader: CompareLeader = a > b ? "a" : "b";
      return [{ ...axis, gap: Math.abs(a - b), leader }];
    })
    .sort((left, right) =>
      left.gap === right.gap ? left.label.localeCompare(right.label) : right.gap - left.gap,
    );

/** How many axes score both products the same; agreement, not a difference. */
export const tiedAxes = (axes: readonly CompareAxis[]): number =>
  axes.filter((axis) => divergence(axis) === 0).length;

/** How many axes carry a score for only one of the two products. */
export const oneSidedAxes = (axes: readonly CompareAxis[]): number =>
  axes.filter((axis) => divergence(axis) === null).length;

/** One product's evaluation of one capability, wherever its topic sits. */
const capabilityEvaluation = (snapshot: ReportSnapshot, product: string, capability: string) =>
  snapshot.evaluation.products
    .find((entry) => entry.product === product)
    ?.topics.flatMap((topic) => topic.capabilities)
    .find((entry) => entry.capability === capability);

/**
 * What an axis is made of, so a reader can see why one product scores higher.
 *
 * A text question is left out. It never resolves to a number, so it cannot move
 * a score and has no distance to rank by; it belongs to the knowledge base, not
 * to an explanation of a gap.
 *
 * Everything deterministic stays, answered or not. A question neither product
 * answered is as much a part of the picture as the one they disagree on, and
 * dropping it would let a thin capability read as a thoroughly examined one.
 * Ranking is the caller's to apply, and `byImpact` is the order the rest of the
 * screen uses.
 */
export const breakdown = (
  snapshot: ReportSnapshot,
  level: CompareLevel,
  axis: string,
  a: string,
  b: string,
): CompareAxis[] => {
  if (level === "topic") {
    return snapshot.input.capabilities
      .filter((capability) => capability.topic === axis)
      .map((capability) => ({
        id: capability.id,
        label: capability.label,
        group: capability.label,
        a: capabilityEvaluation(snapshot, a, capability.id)?.score ?? null,
        b: capabilityEvaluation(snapshot, b, capability.id)?.score ?? null,
      }));
  }

  const capability = snapshot.input.capabilities.find((entry) => entry.id === axis);
  if (capability === undefined) return [];

  const valuesOf = (product: string): Map<string, number> =>
    new Map(
      (capabilityEvaluation(snapshot, product, axis)?.answers ?? []).map((answer) => [
        answer.question,
        answer.value,
      ]),
    );
  const left = valuesOf(a);
  const right = valuesOf(b);

  /* Unknown to the evaluation is not the same as informational, so it stays. */
  const contextual = (question: string): boolean =>
    snapshot.input.questions.find((entry) => entry.id === question)?.answer_type.kind === "text";

  return capability.questions
    .filter((question) => !contextual(question))
    .map((question) => ({
      id: question,
      label: snapshot.input.questions.find((entry) => entry.id === question)?.title ?? question,
      group: capability.label,
      a: left.get(question) ?? null,
      b: right.get(question) ?? null,
    }));
};

/**
 * One side's answer to a question, with the record it was resolved from.
 *
 * The evaluation keeps only what scores: the value, who gave it and where it
 * came from. The reasoning behind the value lives on the answer record in the
 * snapshot's input, so the two are joined here by the answer's id.
 */
export type AnswerDetail = {
  resolved: ResolvedAnswer;
  /** Missing only in a snapshot whose input was written without its answers. */
  record: Answer | undefined;
};

/** One question as the pair answered it, with the place it holds in the catalogue. */
export type QuestionScope = {
  question: Question | undefined;
  capability: Capability | undefined;
  topic: Topic | undefined;
  a: AnswerDetail | null;
  b: AnswerDetail | null;
};

/** Whether the author wrote down why they answered the way they did. */
export const explanationOf = (detail: AnswerDetail | null): string | null => {
  const text = detail?.record?.explanation?.trim() ?? "";
  return text === "" ? null : text;
};

/** The editorial remark beside an answer, when there is one. */
export const commentOf = (detail: AnswerDetail | null): string | null => {
  const text = detail?.record?.comment?.trim() ?? "";
  return text === "" ? null : text;
};

const detailOf = (
  snapshot: ReportSnapshot,
  product: string,
  capability: string,
  question: string,
): AnswerDetail | null => {
  const resolved = capabilityEvaluation(snapshot, product, capability)?.answers.find(
    (answer) => answer.question === question,
  );
  if (resolved === undefined) return null;
  return {
    resolved,
    record: snapshot.input.answers.find((answer) => answer.id === resolved.answer),
  };
};

/**
 * Everything the reader needs to see why one question came out the way it did
 * for the two products: the question itself, where it sits, and each side's
 * answer with its reasoning.
 */
export const questionScope = (
  snapshot: ReportSnapshot,
  capability: string,
  question: string,
  a: string,
  b: string,
): QuestionScope => {
  const owner = snapshot.input.capabilities.find((entry) => entry.id === capability);
  return {
    question: snapshot.input.questions.find((entry) => entry.id === question),
    capability: owner,
    topic: snapshot.input.topics.find((entry) => entry.id === owner?.topic),
    a: detailOf(snapshot, a, capability, question),
    b: detailOf(snapshot, b, capability, question),
  };
};
